<script lang="ts">
  import { onMount } from 'svelte';
  import Prose from '../lib/components/Prose.svelte';
  import FlamePanel from '../lib/algorithms/thinking-traces/FlamePanel.svelte';
  import type { AnnotatedTrace } from '../lib/data/annotated-traces';
  import type { Category } from '../lib/design/categories';

  type PairTrace = AnnotatedTrace & {
    slot: 'pass' | 'fail';
    task: string;
    truth: number;
    reported: number;
    passed: boolean;
    errorIndex: number | null;
  };
  type PairData = { traces: PairTrace[] };

  let data = $state<PairData | null>(null);

  onMount(async () => {
    const res = await fetch('./opus-m14983-pair.json');
    data = await res.json();
  });

  const passTrace = $derived(
    data ? data.traces.find((t) => t.slot === 'pass') ?? null : null,
  );
  const failTrace = $derived(
    data ? data.traces.find((t) => t.slot === 'fail') ?? null : null,
  );

  function countCat(t: PairTrace | null, cat: Category): number {
    return t ? t.rows.filter((r) => r.category === cat).length : 0;
  }
</script>

<section id="thinking-ops" class="section">
  <Prose>
    <header class="header">
      <div class="eyebrow">Thinking</div>
      <h2>Verification is not a second engine</h2>
      <p class="summary">
        Two Opus runs on nearly the same task: one succeeds, one fails. The
        failed run verifies more, and still ends wrong.
      </p>
    </header>
    <p>
      Both runs compute <em>a<sup>b</sup> mod 14,983</em>. The exponent differs
      by one: probe 195 used 22 and passed; probe 198 used 21 and failed. Same
      model, same algorithm, nearly the same task. The difference is inside the
      generated reasoning trace.
    </p>

    <h3 class="subhead">What thinking tokens are</h3>
    <p>
      A reasoning model still generates tokens. Thinking tokens are extra text
      before the final answer, sampled one token at a time by the same
      autoregressive process as everything else. Each token depends on the prior
      tokens. The trace is one continuous sequence, not a set of separate
      modules. Reinforcement learning shaped which continuations became likely:
      reward came from correct final answers, not from a supervised checklist of
      reasoning moves.
    </p>
    <p>
      The colored categories are my labels, not the model's architecture. When
      the trace says "let me double-check," no separate verification subsystem
      turns on. The same next-token distribution has produced text that looks
      like verification. Because it all runs through the same weights, a
      verification segment cannot be more reliable than the operations those
      weights can perform. More verification-labeled text means more text in
      that register, not an independent audit.
    </p>
  </Prose>

  {#if passTrace && failTrace}
    <div class="pair">
      <FlamePanel
        trace={passTrace}
        titleOverride={passTrace.task}
        outcome={{ label: `correct · ${passTrace.reported.toLocaleString()}`, tone: 'ok' }}
      />

      <FlamePanel
        trace={failTrace}
        titleOverride={failTrace.task}
        outcome={{
          label: `wrong · ${failTrace.reported.toLocaleString()} · truth ${failTrace.truth.toLocaleString()}`,
          tone: 'err',
        }}
        initialSelectedIndex={failTrace.errorIndex}
        errorIndex={failTrace.errorIndex}
      />
    </div>

    <Prose>
      <p>
        Count the verification segments. The successful run has
        <strong>{countCat(passTrace, 'VERIFICATION')}</strong>. The failing run
        has <strong>{countCat(failTrace, 'VERIFICATION')}</strong>, including
        three separate checks at the end. The answer is still wrong.
      </p>
      <p>
        The highlighted failing segment is 15. Opus asserts
        <code>result = 14,707</code> for bit 2 of the exponent. The correct
        value is <code>724</code>. That single wrong multiplication sits in the
        middle of the procedure, and every later step inherits it. When Opus
        verifies the final answer in segments 20 and 21, it recomputes from the
        same wrong intermediates with the same weights. The check confirms a
        consistent wrong chain.
      </p>
      <p>
        This is the ceiling of same-model verification. It is not a safety net
        running on a different engine. It is the model re-running operations with
        the same error profile. If the failure is inside an operation the weights
        do not reliably execute, doing the operation again can reproduce the
        mistake. What helps is a check whose errors are not correlated with the
        generator's: a tool, a genuinely different decomposition, or another
        model. This run had none of those, only more tokens in the same voice.
      </p>
      <p>
        This also shows where the independence approximation is useful, and
        where it stops being literal. Across many runs, step errors compound
        roughly exponentially, so <em>P(success) = p<sub>step</sub><sup>N</sup></em>
        predicts aggregate pass rates. Inside one trace, the steps are not
        independent rolls. They are stretches of one continuous stream on the
        same weights. Verification is not a second opinion; it is the same voice
        asked again.
      </p>
    </Prose>
  {/if}
</section>

<style>
  .section {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: var(--space-4xl);
    width: 100%;
  }
  .header {
    margin-bottom: var(--space-lg);
  }
  .eyebrow {
    font-family: var(--font-sans);
    font-size: var(--text-xs);
    font-weight: var(--weight-medium);
    color: var(--ink-muted);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: var(--space-sm);
  }
  .summary {
    font-family: var(--font-serif);
    font-style: italic;
    font-size: var(--text-lg);
    color: var(--ink-muted);
    line-height: var(--leading-snug);
    margin-top: var(--space-sm);
    margin-bottom: var(--space-lg);
  }
  :global(.prose) h3.subhead {
    font-family: var(--font-sans);
    font-size: var(--text-base);
    font-weight: var(--weight-medium);
    color: var(--ink);
    margin-top: var(--space-xl);
    margin-bottom: var(--space-sm);
  }

  .pair {
    width: 100%;
    max-width: 880px;
    display: flex;
    flex-direction: column;
    gap: var(--space-lg);
    margin: var(--space-lg) 0;
  }
</style>
